import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UNIQUE_DEVICE_ID } from 'constants/authConstants';
import { INPUT_DOCUMENT_SAVE_DEBOUNCE_MS } from 'constants/appConstants';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { GcVariety, GUnit, MergedCrop } from 'interfaces/IGlobalCatalogue';
import { IInput } from 'interfaces/IInput';
import { IInputTask } from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InputService from 'services/InputService';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';
import {
  areInputHeadersEqual,
  countChangedHeaderFields,
} from 'utils/FarmUtils';
import {
  buildCropPatch,
  buildUnitPatch,
  buildVarietyPatch,
  buildZoneAreaPatch,
  isInputDraftValid,
  resolveAreaUnit,
  resolveUnitValue,
  toCalendarDateIso,
} from 'utils/inputDraftPatches';
import { getApiErrorMessage } from 'utils/UtilityMethods';
import { INPUT_DOCUMENT_UPDATE_ERROR_MESSAGE } from 'constants/messageConstants';
import { getUserDisplayName } from 'utils/UserUtils';
import { AREA_UNIT_FILTER, filterGUnitsByFilter } from 'utils/GUnitFilters';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import { useCropCatalogue } from 'hooks/useCropCatalogue';

// Stable default: the units query yields `undefined` while loading, while
// disabled and on error, so an inline `= []` would mint a new array on every
// render — which would change `toHeaderPayload`'s identity and restart the
// autosave debounce each time.
const EMPTY_G_UNITS: GUnit[] = [];

interface UseInputDocumentDraftProps {
  selectedInput: IInput | undefined;
  activeOrganisationId: string | null;
  userProfile: PartialUserProfile | undefined;
  gOrgId: string | null;
  mOrgId: string | null;
}

export const useInputDocumentDraft = ({
  selectedInput,
  activeOrganisationId,
  userProfile,
  gOrgId,
  mOrgId,
}: UseInputDocumentDraftProps) => {
  const queryClient = useQueryClient();
  const deviceIdRef = useRef(
    (() => {
      try {
        return localStorage.getItem(UNIQUE_DEVICE_ID);
      } catch {
        return null;
      }
    })()
  );

  const [draft, setDraft] = useState<IInput | null>(null);
  const headerDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const latestRequestIdRef = useRef(0);
  const lastSavedDraftRef = useRef<IInput | null>(null);
  const lastScheduledDraftKeyRef = useRef<string | null>(null);
  const [lastErrorMessage, setLastErrorMessage] = useState<string | null>(null);
  const [hasFailedUpdate, setHasFailedUpdate] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [headerLastSavedAt, setHeaderLastSavedAt] = useState<number | null>(
    null
  );

  // Holds the MergedCrop selected from search results until the browse list loads it.
  const selectedFromSearchRef = useRef<MergedCrop | null>(null);

  const {
    items: visibleCrops,
    allMergedItems: mergedCrops,
    searchItems: cropSearchItems,
    isLoading: isCropsLoading,
    isPaginating: isCropsPaginating,
    hasNextPage: hasNextCropPage,
    fetchNextPage,
    searchTerm: cropSearchTerm,
    onSearch: onCropSearch,
    clearSearch: clearCropSearch,
  } = useCropCatalogue(gOrgId, mOrgId, draft?.crop?.globalId ?? null);

  // Unscoped by parameter — other consumers (e.g. Input Steps grid's Rate
  // Unit) need GUnit parameters beyond Area, so this hook fetches the full
  // active list and filters locally for its own (Area-only) needs below.
  const { data: gUnits = EMPTY_G_UNITS } = useFetchGUnits(gOrgId);
  const areaGUnits = useMemo(
    () => filterGUnitsByFilter(gUnits, AREA_UNIT_FILTER),
    [gUnits]
  );

  const fetchNextCropPage = useCallback(() => {
    if (!isCropsPaginating && hasNextCropPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextCropPage, isCropsPaginating]);

  const resetErrorState = useCallback(() => {
    setLastErrorMessage(null);
    setHasFailedUpdate(false);
    setIsRetrying(false);
  }, []);

  const ensureOriginalStored = useCallback((baseline: IInput) => {
    if (!BrowserStorageUtils.getOriginalInput(baseline.id)) {
      BrowserStorageUtils.storeOriginalInput(baseline);
    }
  }, []);

  useEffect(() => {
    if (!selectedInput) {
      setDraft(null);
      lastSavedDraftRef.current = null;
      selectedFromSearchRef.current = null;
      latestRequestIdRef.current = 0;
      lastScheduledDraftKeyRef.current = null;
      resetErrorState();
      setHeaderLastSavedAt(null);
      return;
    }

    const editedInput = BrowserStorageUtils.getEditedInput(selectedInput.id);
    setDraft(editedInput ?? selectedInput);
    lastSavedDraftRef.current = selectedInput;
    latestRequestIdRef.current = 0;
    lastScheduledDraftKeyRef.current = null;
    resetErrorState();
    if (selectedInput.dateModified) {
      const parsed = Date.parse(selectedInput.dateModified);
      setHeaderLastSavedAt(Number.isNaN(parsed) ? Date.now() : parsed);
    } else {
      setHeaderLastSavedAt(Date.now());
    }
  }, [resetErrorState, selectedInput?.id]);

  const selectedCropOption = useMemo((): MergedCrop | undefined => {
    if (!draft) return undefined;
    const fromBrowse = mergedCrops.find(
      (c) =>
        c.globalId === draft.crop?.globalId || c.name === draft.crop?.cropName
    );
    if (fromBrowse) return fromBrowse;
    // Fall back to a crop selected via search while the browse list catches up.
    if (selectedFromSearchRef.current?.globalId === draft.crop?.globalId) {
      return selectedFromSearchRef.current;
    }
    return undefined;
  }, [draft, mergedCrops]);

  const varietyOptions = useMemo(
    (): GcVariety[] => selectedCropOption?.varieties ?? [],
    [selectedCropOption]
  );

  const unitValue = useMemo(
    () => resolveUnitValue(draft?.zone, areaGUnits),
    [draft?.zone, areaGUnits]
  );

  const updateMutation = useMutation({
    mutationFn: async (payload: IInput) => {
      const clientNow = new Date().toISOString();
      return InputService.UpdateDoc(payload, clientNow);
    },
  });

  const { mutate } = updateMutation;

  // Shapes the outgoing UpdateDoc payload from a full draft: strips tasks
  // (backend excludes them from the $set anyway) and stamps modification
  // metadata. The draft/BrowserStorage copy itself keeps its real tasks.
  // The area unit's globalId is repaired on every save, so a document that
  // inherited an absent or empty-GUID unit from its Zone/Farm is corrected by
  // any subsequent edit rather than only by re-picking the unit by hand.
  const toHeaderPayload = useCallback(
    (input: IInput): IInput => {
      const modifierName = getUserDisplayName(
        userProfile?.firstName,
        userProfile?.lastName
      );

      return {
        ...input,
        sourceId: deviceIdRef.current ?? input.sourceId,
        dateModified: new Date().toISOString(),
        modifierId: userProfile?.id ?? input.modifierId ?? null,
        snapModifier: modifierName || input.snapModifier || null,
        zone: input.zone
          ? {
              ...input.zone,
              areaUnit: resolveAreaUnit(input.zone.areaUnit, areaGUnits),
            }
          : input.zone,
        tasks: [],
      };
    },
    [areaGUnits, userProfile?.firstName, userProfile?.id, userProfile?.lastName]
  );

  const applyHeaderServerUpdate = useCallback(
    (updatedInput: IInput) => {
      if (!activeOrganisationId) return;

      queryClient.setQueryData<IInput[]>(
        [INPUTS_QUERY_KEY, activeOrganisationId],
        (current) => {
          if (!current) return current;
          return current.map((item) =>
            item.id === updatedInput.id ? updatedInput : item
          );
        }
      );

      // Preserve the current draft's tasks — a task add/edit may still be
      // in flight and the header response's task snapshot can be stale.
      setDraft((prev) => {
        if (!prev) return updatedInput;
        const merged = { ...updatedInput, tasks: prev.tasks };
        BrowserStorageUtils.storeEditedInput(merged);
        return merged;
      });
      lastSavedDraftRef.current = updatedInput;
      // Advance the rollback baseline to post-save server state so Discard
      // only reverts remaining unsaved changes (e.g. pending task edits).
      BrowserStorageUtils.storeOriginalInput(updatedInput);
      resetErrorState();
      if (updatedInput.dateModified) {
        const parsed = Date.parse(updatedInput.dateModified);
        setHeaderLastSavedAt(Number.isNaN(parsed) ? Date.now() : parsed);
      } else {
        setHeaderLastSavedAt(Date.now());
      }
    },
    [activeOrganisationId, queryClient, resetErrorState]
  );

  const executeHeaderUpdate = useCallback(
    (payload: IInput, options?: { retrying?: boolean }) => {
      const requestId = ++latestRequestIdRef.current;

      if (options?.retrying) {
        resetErrorState();
        setIsRetrying(true);
      }

      mutate(payload, {
        onSuccess: (response) => {
          if (requestId !== latestRequestIdRef.current) return;

          const updatedInput = response?.data?.[0];
          if (!updatedInput) return;

          setIsRetrying(false);
          applyHeaderServerUpdate(updatedInput);
        },
        onError: (error) => {
          if (requestId !== latestRequestIdRef.current) return;
          const message = getApiErrorMessage(
            error,
            INPUT_DOCUMENT_UPDATE_ERROR_MESSAGE
          );
          setLastErrorMessage(message);
          setHasFailedUpdate(true);
          setIsRetrying(false);
        },
      });
    },
    [applyHeaderServerUpdate, mutate, resetErrorState]
  );

  const hasUnsavedHeaderChanges = useMemo(
    () => !areInputHeadersEqual(draft, lastSavedDraftRef.current),
    [draft]
  );

  const headerChangeCount = useMemo(
    () => countChangedHeaderFields(draft, lastSavedDraftRef.current),
    [draft]
  );

  useEffect(() => {
    if (hasUnsavedHeaderChanges) {
      setHeaderLastSavedAt(null);
      resetErrorState();
    }
  }, [hasUnsavedHeaderChanges, resetErrorState]);

  const isDraftValid = useMemo(() => isInputDraftValid(draft), [draft]);

  // Stores the pre-edit snapshot on the first header change of a session.
  // Task mutators (addDraftTask/updateDraftTask/removeDraftTask) store their
  // own snapshot on first task change via ensureOriginalStored, so this only
  // needs to cover the header-edit path — it must NOT clear BrowserStorage
  // when there are no header changes, since a task-only edit would then get
  // wiped out from under it.
  useEffect(() => {
    if (!selectedInput || !hasUnsavedHeaderChanges) return;
    ensureOriginalStored(selectedInput);
  }, [ensureOriginalStored, hasUnsavedHeaderChanges, selectedInput]);

  useEffect(() => {
    if (!draft || !selectedInput || !hasUnsavedHeaderChanges) return;

    const draftKey = JSON.stringify(draft);
    if (draftKey !== lastScheduledDraftKeyRef.current) {
      lastScheduledDraftKeyRef.current = draftKey;
      resetErrorState();
    }

    BrowserStorageUtils.storeEditedInput(draft);

    if (headerDebounceTimeoutRef.current) {
      clearTimeout(headerDebounceTimeoutRef.current);
    }

    if (!isDraftValid) {
      return;
    }

    const scheduleSyncAttempt = () => {
      const latestStored = BrowserStorageUtils.getEditedInput(selectedInput.id);
      if (!latestStored) return;

      if (lastScheduledDraftKeyRef.current !== draftKey) return;
      executeHeaderUpdate(toHeaderPayload(latestStored));
    };

    headerDebounceTimeoutRef.current = setTimeout(
      scheduleSyncAttempt,
      INPUT_DOCUMENT_SAVE_DEBOUNCE_MS
    );

    return () => {
      if (headerDebounceTimeoutRef.current) {
        clearTimeout(headerDebounceTimeoutRef.current);
        headerDebounceTimeoutRef.current = null;
      }
    };
  }, [
    draft,
    executeHeaderUpdate,
    hasUnsavedHeaderChanges,
    isDraftValid,
    resetErrorState,
    selectedInput,
    toHeaderPayload,
  ]);

  useEffect(() => {
    return () => {
      if (headerDebounceTimeoutRef.current) {
        clearTimeout(headerDebounceTimeoutRef.current);
        headerDebounceTimeoutRef.current = null;
      }
    };
  }, [selectedInput?.id]);

  const updateDraft = useCallback((patch: Partial<IInput>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const handleCropChange = useCallback(
    (value: string) => {
      const fromBrowse = mergedCrops.find((c) => c.globalId === value);
      const fromSearch = cropSearchItems.find((c) => c.globalId === value);
      const option = fromBrowse ?? fromSearch;
      if (!option) return;

      if (!fromBrowse && fromSearch) {
        selectedFromSearchRef.current = fromSearch;
      }

      clearCropSearch();
      updateDraft(buildCropPatch(option));
    },
    [updateDraft, mergedCrops, cropSearchItems, clearCropSearch]
  );

  const handleVarietyChange = useCallback(
    (value: string) => {
      const option = varietyOptions.find((v) => v.varietyId === value);
      updateDraft(buildVarietyPatch(draft, option));
    },
    [updateDraft, varietyOptions, draft]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      updateDraft({
        startDate: toCalendarDateIso(date),
      });
    },
    [updateDraft]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      updateDraft({
        endDate: toCalendarDateIso(date),
      });
    },
    [updateDraft]
  );

  const handleZoneAreaChange = useCallback(
    (value: number | null) => {
      updateDraft(buildZoneAreaPatch(draft?.zone, value));
    },
    [updateDraft, draft?.zone]
  );

  const handleUnitChange = useCallback(
    (value: string) => {
      const option = areaGUnits.find((u) => u.globalId === value);
      if (!option) return;
      updateDraft(buildUnitPatch(draft?.zone, option));
    },
    [updateDraft, areaGUnits, draft?.zone]
  );

  const discardChanges = useCallback(() => {
    if (!selectedInput) return;

    const originalInput =
      BrowserStorageUtils.getOriginalInput(selectedInput.id) ?? selectedInput;
    setDraft(originalInput);
    lastSavedDraftRef.current = selectedInput;
    BrowserStorageUtils.removeEditedInput(selectedInput.id);
    BrowserStorageUtils.removeOriginalInput(selectedInput.id);
    lastScheduledDraftKeyRef.current = null;
    resetErrorState();
    if (selectedInput.dateModified) {
      const parsed = Date.parse(selectedInput.dateModified);
      setHeaderLastSavedAt(Number.isNaN(parsed) ? Date.now() : parsed);
    } else {
      setHeaderLastSavedAt(Date.now());
    }
  }, [resetErrorState, selectedInput]);

  const retryLastHeaderUpdate = useCallback(() => {
    if (!selectedInput) return;
    const latestStored = BrowserStorageUtils.getEditedInput(selectedInput.id);
    if (!latestStored) return;

    if (headerDebounceTimeoutRef.current) {
      clearTimeout(headerDebounceTimeoutRef.current);
      headerDebounceTimeoutRef.current = null;
    }

    executeHeaderUpdate(toHeaderPayload(latestStored), { retrying: true });
  }, [executeHeaderUpdate, selectedInput, toHeaderPayload]);

  const saveHeaderNow = useCallback(() => {
    if (!draft || !selectedInput) return;
    if (!isDraftValid) return;

    BrowserStorageUtils.storeEditedInput(draft);

    if (headerDebounceTimeoutRef.current) {
      clearTimeout(headerDebounceTimeoutRef.current);
      headerDebounceTimeoutRef.current = null;
    }

    executeHeaderUpdate(toHeaderPayload(draft));
  }, [
    draft,
    executeHeaderUpdate,
    isDraftValid,
    selectedInput,
    toHeaderPayload,
  ]);

  const addDraftTask = useCallback(
    (task: IInputTask) => {
      if (!selectedInput) return;
      ensureOriginalStored(selectedInput);
      setDraft((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, tasks: [...(prev.tasks ?? []), task] };
        BrowserStorageUtils.storeEditedInput(updated);
        return updated;
      });
    },
    [ensureOriginalStored, selectedInput]
  );

  const updateDraftTask = useCallback(
    (updatedTask: IInputTask) => {
      if (!selectedInput) return;
      ensureOriginalStored(selectedInput);
      setDraft((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          tasks: (prev.tasks ?? []).map((t) =>
            t.taskId === updatedTask.taskId ? updatedTask : t
          ),
        };
        BrowserStorageUtils.storeEditedInput(updated);
        return updated;
      });
    },
    [ensureOriginalStored, selectedInput]
  );

  const removeDraftTask = useCallback(
    (taskId: string) => {
      if (!selectedInput) return;
      ensureOriginalStored(selectedInput);
      setDraft((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          tasks: (prev.tasks ?? []).filter((t) => t.taskId !== taskId),
        };
        BrowserStorageUtils.storeEditedInput(updated);
        return updated;
      });
    },
    [ensureOriginalStored, selectedInput]
  );

  return {
    draft,
    selectedCropOption,
    varietyOptions,
    unitValue,
    mergedCrops: visibleCrops,
    gUnits: gUnits as GUnit[],
    isCropsLoading,
    isCropsPaginating,
    hasNextCropPage,
    fetchNextCropPage,
    cropSearchTerm,
    onCropSearch,
    hasUnsavedHeaderChanges,
    lastErrorMessage,
    hasFailedUpdate,
    isRetrying,
    headerLastSavedAt,
    isDraftValid,
    headerChangeCount,
    isSaving: updateMutation.isPending,
    handleCropChange,
    handleVarietyChange,
    handleStartDateChange,
    handleEndDateChange,
    handleZoneAreaChange,
    handleUnitChange,
    discardChanges,
    retryLastHeaderUpdate,
    saveHeaderNow,
    addDraftTask,
    updateDraftTask,
    removeDraftTask,
  };
};
